import type { ReactElement } from 'react';
import type { ComplianceStatusResponse, GetFanClubStatusResponse, ShowSummary, ShowViewerRole, SpyMode } from '@fetmode/types';
import type { createApiClient } from '@fetmode/api-client';
import { ApiError } from '@fetmode/api-client';
import { useAuthModal } from '@fetmode/app-core';
import { Button } from '@fetmode/ui';

import { parseJoinRestriction } from './show-join-restriction.js';
import { hasShowFloorAccess, resolveShowPlayerGate } from './show-join-gate.js';
import { useCoinShop } from '../wallet/index.js';

type ApiClient = ReturnType<typeof createApiClient>;

export interface ShowPlayerGateProps {
  show: ShowSummary;
  showId: string;
  username: string;
  user: { id: string } | null;
  api: ApiClient;
  fanClub: GetFanClubStatusResponse | null;
  isJoining: boolean;
  compliance: ComplianceStatusResponse | null;
  countdownLabel: string | null;
  onJoiningChange: (joining: boolean) => void;
  onViewerRoleChange: (role: ShowViewerRole) => void;
  onViewerCountChange: (count: number) => void;
  onShowRefresh: () => Promise<void>;
  onError: (message: string | null) => void;
  onCoinBalanceChange: (total: number) => void;
  onFanClubChange: (status: GetFanClubStatusResponse) => void;
  onGetInLine: () => void;
  onManualJoin?: () => void;
  onRoomBanned?: (details: import('./show-join-restriction.js').RoomBanOverlayState) => void;
  onKicked?: (until: string | null) => void;
}

function handleJoinError(
  err: unknown,
  onError: (message: string | null) => void,
  onRoomBanned?: ShowPlayerGateProps['onRoomBanned'],
  onKicked?: ShowPlayerGateProps['onKicked'],
): void {
  const parsed = parseJoinRestriction(err);
  if (parsed.kind === 'banned') {
    onRoomBanned?.(parsed.details);
  }
  if (parsed.kind === 'kicked') {
    onKicked?.(parsed.until);
  }
  onError(parsed.message);
}

export function ShowPlayerGate({
  show,
  showId,
  username,
  user,
  api,
  fanClub,
  isJoining,
  compliance,
  countdownLabel,
  onJoiningChange,
  onViewerRoleChange,
  onViewerCountChange,
  onShowRefresh,
  onError,
  onCoinBalanceChange,
  onFanClubChange,
  onGetInLine,
  onManualJoin,
  onRoomBanned,
  onKicked,
}: ShowPlayerGateProps): ReactElement | null {
  const { requireAuth } = useAuthModal();
  const { requireSpendCoins } = useCoinShop();

  const runJoinShow = (joinAs?: ShowViewerRole, spyMode?: SpyMode): void => {
    const needed =
      spyMode === 'FLASH'
        ? (show.access.flashSpyCoins ?? 0)
        : joinAs === 'SPY'
          ? 0
          : (show.access.entryFeeCoins ?? 0);
    const runJoin = (): void => {
      onManualJoin?.();
      onJoiningChange(true);
      onError(null);

      const body =
        joinAs || spyMode
          ? {
              ...(joinAs ? { joinAs } : {}),
              ...(spyMode ? { spyMode } : {}),
            }
          : {};

      void api
        .joinShow(showId, body)
        .then(async (result) => {
          onViewerCountChange(result.viewerCount);
          onViewerRoleChange(result.joinedRole);
          onError(null);
          await onShowRefresh();
        })
        .catch((err: unknown) => {
          handleJoinError(err, onError, onRoomBanned, onKicked);
        })
        .finally(() => {
          onJoiningChange(false);
        });
    };
    void requireSpendCoins(needed, runJoin).then((ok) => {
      if (ok) {
        runJoin();
      }
    });
  };

  const handleJoinShow = (joinAs?: ShowViewerRole, spyMode?: SpyMode): void => {
    requireAuth(() => {
      runJoinShow(joinAs, spyMode);
    }, 'join');
  };

  const handleJoinFanClub = (): void => {
    requireAuth(() => {
      const runJoinFanClub = (): void => {
        onJoiningChange(true);
        onError(null);

        void api
          .joinFanClub(username)
          .then(async (result) => {
            onCoinBalanceChange(result.wallet.total);
            onFanClubChange({
              monthlyCoins: result.monthlyCoins,
              isMember: true,
              tiers: fanClub?.tiers ?? [],
              memberTierId: result.tierId,
            });
            onError(null);
            if (show.status === 'LIVE' && !hasShowFloorAccess(show)) {
              try {
                const joinResult = await api.joinShow(showId);
                onViewerCountChange(joinResult.viewerCount);
                onViewerRoleChange(joinResult.joinedRole);
              } catch (err: unknown) {
                handleJoinError(err, onError, onRoomBanned, onKicked);
              }
            }
            await onShowRefresh();
          })
          .catch((err: unknown) => {
            onError(err instanceof ApiError ? err.message : 'Unable to join fan club.');
          })
          .finally(() => {
            onJoiningChange(false);
          });
      };
      void requireSpendCoins(fanClub?.monthlyCoins ?? 0, runJoinFanClub).then((ok) => {
        if (ok) {
          runJoinFanClub();
        }
      });
    }, 'fanClub');
  };

  const gate = resolveShowPlayerGate({
    show,
    user,
    fanClub,
    isJoining,
    compliance,
    countdownLabel,
    actions: {
      onJoinShow: () => {
        handleJoinShow();
      },
      onJoinSpy: () => {
        handleJoinShow('SPY', 'ONGOING');
      },
      onJoinFlashSpy: () => {
        handleJoinShow('SPY', 'FLASH');
      },
      onJoinFanClub: handleJoinFanClub,
      onGetInLine: () => {
        requireAuth(() => {
          onGetInLine();
        }, 'private');
      },
    },
  });

  if (!gate.visible) {
    return null;
  }

  const secondaryActions =
    gate.secondaryActions ?? (gate.secondaryAction ? [gate.secondaryAction] : []);
  const placement = gate.placement ?? 'bottom';

  return (
    <div
      className={[
        'show-room__player-gate',
        `show-room__player-gate--${placement}`,
        gate.tone ? `show-room__player-gate--${gate.tone}` : null,
      ]
        .filter(Boolean)
        .join(' ')}
      role="region"
      aria-live="polite"
    >
      <div className="show-room__player-gate-card">
        <div className="show-room__player-gate-copy">
          <h2 className="show-room__player-gate-title">{gate.title}</h2>
          {gate.hint ? <p className="show-room__player-gate-hint">{gate.hint}</p> : null}
        </div>

        {gate.primaryAction || secondaryActions.length > 0 ? (
          <div className="show-room__player-gate-actions">
            {gate.primaryAction ? (
              <div className="show-room__player-gate-primary">
                <Button
                  label={gate.primaryAction.label}
                  variant={gate.primaryAction.variant ?? 'primary'}
                  disabled={isJoining && show.type !== 'EXCLUSIVE'}
                  onClick={gate.primaryAction.onClick}
                />
              </div>
            ) : null}
            {secondaryActions.length > 0 ? (
              <div className="show-room__player-gate-secondary" role="group" aria-label="More options">
                {secondaryActions.map((action) => (
                  <Button
                    key={action.label}
                    label={action.label}
                    variant="ghost"
                    disabled={isJoining && show.type !== 'EXCLUSIVE'}
                    onClick={action.onClick}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
